import { useState } from "react";
import { useReferrals } from "../api/hooks";
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Pill,
  Progress,
  Row,
  Skeleton,
  Stat,
} from "../components/ui";
import { pct, shortDate } from "../lib/format";
import { tg } from "../telegram";

export function ReferralsScreen() {
  const q = useReferrals();
  const [copied, setCopied] = useState(false);

  if (q.isPending) return <Skeleton className="h-64" />;
  if (q.isError) return <ErrorNote>Could not load referrals.</ErrorNote>;
  const d = q.data;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(d.link);
      setCopied(true);
      tg.haptic.light();
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable in some webviews; the share button still works */
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <p className="text-center text-xs uppercase tracking-wide text-hint">Your code</p>
        <p className="my-1 text-center font-mono text-3xl font-bold tracking-widest">{d.code}</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            onClick={() => {
              tg.haptic.light();
              tg.share(d.link, d.shareText);
            }}
          >
            Share
          </Button>
          <Button variant="secondary" onClick={copy}>
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>
      </Card>

      <Card title="Referral bonus">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Bonus today" value={pct(d.bonusBps, true)} />
          <Stat label="Active direct" value={`${d.active.direct} / ${d.totals.direct}`} />
          <Stat label="Active 2nd" value={`${d.active.secondary} / ${d.totals.secondary}`} />
        </div>
        <div className="mt-3">
          <Row label="Progress to cap" value={`${Math.round(d.capProgress * 100)}%`} />
          <Progress value={d.capProgress} />
        </div>
        <div className="mt-3 text-xs text-hint">
          <p>
            {pct(d.rates.directBps, true)} per active direct referral and{" "}
            {pct(d.rates.secondaryBps, true)} per active referral of theirs, up to{" "}
            {pct(d.rates.capBps, true)} ({d.rates.directToCap} active directs).
          </p>
          <p className="mt-1">
            A referral is active if they claimed today or yesterday. Inactive referrals stop
            counting until they claim again.
          </p>
        </div>
      </Card>

      <Card title={`Direct referrals (${d.totals.direct})`}>
        {d.direct.length === 0 ? (
          <Empty>Nobody yet. Share your link to start earning a bonus.</Empty>
        ) : (
          <ul className="divide-y divide-white/5">
            {d.direct.map((r, i) => (
              <li key={i} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="font-medium">{r.displayName}</div>
                  <div className="text-xs text-hint">
                    joined {shortDate(r.joinedAt)}
                    {r.lastClaimDate
                      ? ` · last claim ${shortDate(r.lastClaimDate)}`
                      : " · never claimed"}
                  </div>
                </div>
                <Pill tone={r.active ? "good" : "neutral"}>{r.active ? "active" : "inactive"}</Pill>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
